import React from "react";
import { StyledBadge, StyledIconButton } from "./styles";
import { ITAIconButtonProps } from "./types";

// TAIconButton.tsx

/**
 * TAIconButton Component
 *
 * A reusable icon button component that supports size variants, selection state,
 * and an optional badge indicator. The component allows customization of styles
 * and icons based on the selected state.
 *
 * @param {Object} props - The component props.
 * @param {React.ReactElement<SvgIconProps>} props.icon - The icon to be displayed inside the button. Must be a valid SvgIcon element.
 * @param {boolean} [props.selected=false] - If true, the button is in a selected state, changing the icon's color and the button's background.
 * @param {boolean} [props.showBadge=false] - If true, a badge will be displayed over the button.
 * @param {number|string} [props.badgeContent] - The content to display inside the badge if `showBadge` is true (e.g., a number or string).
 * @param {"lg" | "md"} [props.variant="md"] - Defines the size variant of the button ("lg" for large, "md" for medium).
 * @param {"dot"} [props.badgeVariant] - The variant of the badge ("dot" for a small dot badge).
 *
 * @returns {JSX.Element} The rendered TAIconButton component.
 *
 * @example
 * const icon = <SvgIcon><path d="M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z"/></SvgIcon>;
 *
 * <TAIconButton
 *   icon={icon}
 *   selected={true}
 *   showBadge={true}
 *   badgeContent={5}
 *   variant="lg"
 *   badgeVariant="dot"
 * />
 */

const TAIconButton: React.FC<ITAIconButtonProps> = ({
  icon,
  selected = false,
  showBadge = false,
  badgeContent,
  variant = "md",
  badgeVariant,
  ...props
}) => {
  // Clone the icon and apply custom styles if the button is selected
  const coloredIcon = selected
    ? React.cloneElement(icon, {
        sx: { color: "white" },
      })
    : icon;

  return (
    <StyledIconButton
      selected={selected}
      variant={variant}
      {...props}
      disableRipple
    >
      {showBadge ? (
        <StyledBadge
          badgeContent={badgeContent}
          color="secondary"
          overlap="circular"
          badgeSize={variant}
          badgeVariant={badgeVariant}
        >
          {coloredIcon}
        </StyledBadge>
      ) : (
        coloredIcon
      )}
    </StyledIconButton>
  );
};

export default TAIconButton;
// export default React.memo(TAIconButton);
